import { Room, TwilioError } from 'twilio-video';
import { useEffect } from 'react';

import { Callback } from '../../../types';
import {
  captureTwilioDuplicateIdentityDiagnostic,
  captureTwilioRoomDisconnectedDiagnostic,
  captureTwilioRoomNotFoundDiagnostic,
} from '../../../logging/twilioDiagnostics';

const DUPLICATE_IDENTITY_ERROR_CODE = 53205;
const ROOM_NOT_FOUND_ERROR_CODE = 53106;

export default function useHandleRoomDisconnectionErrors(room: Room, onError: Callback) {
  useEffect(() => {
    const onDisconnected = (disconnectedRoom: Room, error: TwilioError) => {
      if (!error) {
        return;
      }

      const context = {
        roomName: disconnectedRoom?.name ?? null,
        roomSid: disconnectedRoom?.sid ?? null,
        roomState: disconnectedRoom?.state ?? null,
      };

      // 53205 (duplicate identity) is recoverable — a lingering participant gets
      // kicked when a new connect lands with the same identity. Surfacing the
      // global error dialog traps the user on a modal until reload, so suppress
      // it here and let the per-page reconnect logic take over. We still record
      // the event in Sentry so we can track how often it happens.
      if (error.code === DUPLICATE_IDENTITY_ERROR_CODE) {
        captureTwilioDuplicateIdentityDiagnostic(error, 'room_disconnected', context);
        return;
      }

      // Errors delivered after a successful connect used to go straight to the
      // global error dialog without any trace in Sentry. Record them (with the room
      // they happened in) so a disconnect-time 53106 or a 53001/53002 signaling
      // drop on either page becomes visible. Dialog behaviour is unchanged.
      if (error.code === ROOM_NOT_FOUND_ERROR_CODE) {
        captureTwilioRoomNotFoundDiagnostic(error, context, null, 'room_disconnected');
      } else {
        captureTwilioRoomDisconnectedDiagnostic(error, context);
      }

      onError(error);
    };

    room.on('disconnected', onDisconnected);
    return () => {
      room.off('disconnected', onDisconnected);
    };
  }, [room, onError]);
}
